import { Helmet } from "react-helmet";
import { connect, useDispatch, useSelector } from "react-redux";
import { State } from "redux/store";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import {
  getFilteredNavigationCluster,
  getFilteredSimilarityClusters,
  getIsCardslistLoading,
  getIsImageUploading,
  getNavigationClusterLoader,
  getSimilarityVertexCluster,
  getTotalPagesForCluster,
} from "redux/SingleDataset/selectors";
import {
  getImageData,
  getImageLabels,
  getShowLearnMoreModal,
  getShowRequestDemoModal,
} from "redux/Modals/selectors";
import {
  fetchClusterPagePagination,
  fetchDataPagePagination,
  fetchDataset,
  fetchNavigationCluster,
  fetchSimilarityData,
  fetchSimilarityThresholdsInfo,
  resetStore,
  setCardslistLoading,
  setDatasetLabels,
  setNavigationCluster,
  setNavigationContext,
  setScrollApplied,
  setSimilarityData,
  setSimilarityVertex,
} from "redux/SingleDataset/actions";
import { fetchImageInfo, fetchImageUniquenessData } from "redux/Modals/actions";
import { fetchImageCustomMetadata } from "redux/SingleImage/actions";
import {
  CLUSTER_DETAILS_PAGE,
  DATA_PAGE,
  IMAGE_DETAILS_PAGE,
} from "helpers/constants/pages";
import { convertURItoNumber } from "helpers/utility/utilities";
import {
  FLOW_PARAMETER,
  FOLDER_PARAMETER,
  OBJECT_ID_QUERY_PARAMETER,
  PAGE_PARAMETER,
  SEED_PARAMETER,
  VERTEX_ID,
  VERTEX_TYPE,
} from "helpers/constants/miscellaneous";

import RequestDemoModal from "views/modals/RequestDemoModal";

import ContainerHeader from "views/components/ContainerHeader";
import ImageView from "./ImageView";
import CardsView from "./CardsView";

import styles from "./style.module.scss";
import { ContactUsComponentType } from "types";
import SignupTeaserModal from "views/modals/SignupTeaserModal";
import { LearnMoreModal } from "views/modals/LearnMoreModal";
import useQueryParam from "hooks/useQueryParam";
import { DataPageRightPanelProvider } from "../../providers/DataPageRightPanelProvider";
import { VLChatWrapper } from "../../components/VLChat/VLChatWrapper";
import AddMediaCompletedModal from "../../components/AddMediaCompletedModal";
import { useUserConfig } from "../../../hooks/useUserConfig";
import { useFeatureFlagService } from "../../../hooks/useFeatureFlagService";
import { UNIQUENESS_SCORE } from "../../../helpers/constants/filters";

const DataPage = ({
  clusters,
  isCardListLoading,
  navigationCluster,
  navigationClusterLoader,
  imageData,
  labels,
  isImageUploading,
  showRequestDemoModal,
  showLearnMoreModal,
}: DataPageProps) => {
  const [reviewCounters, setReviewCounters] =
    useState<React.ReactElement | null>(null);
  const queryParamsFilters = useAllQueryParamsFilters();
  const [commonDataFetched, setCommonDataFetched] = useState(false);
  const { datasetId, clusterId, imageId } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const selectedLabelID = useQueryParam(OBJECT_ID_QUERY_PARAMETER);
  const isUniquenessEnabled = useFeatureFlagService(UNIQUENESS_SCORE).enabled;
  const { refetch: refetchUserConfig } = useUserConfig();

  const isDataPage = !!datasetId && !imageId && !clusterId;
  const isClusterPage = !!datasetId && !!clusterId && !imageId;
  const isImagePage = !!imageId;

  const dynamicPage = isImagePage
    ? IMAGE_DETAILS_PAGE
    : isClusterPage
      ? CLUSTER_DETAILS_PAGE
      : DATA_PAGE;

  const navigate = useNavigate();

  const createParams = (searchParams: URLSearchParams) => {
    const params: { [key: string]: any } = {
      [FOLDER_PARAMETER]: null,
      [PAGE_PARAMETER]: null,
    };

    searchParams.forEach((value: string, key: string) => {
      switch (key) {
        case FOLDER_PARAMETER:
          params[FOLDER_PARAMETER] = decodeURIComponent(value);
          break;
        case PAGE_PARAMETER:
          if (convertURItoNumber(value)) {
            params[PAGE_PARAMETER] = convertURItoNumber(value);
          }
          break;
        default:
          break;
      }
    });

    return params;
  };

  const fetchCommonData = async () => {
    try {
      dispatch(setCardslistLoading(true));
      dispatch(setDatasetLabels([]));
      await Promise.all([
        // items in this list are blocking the page loading so the list should be the bare minimum that is required
        //  to render the page and not nothing else (additional settings can be loaded in the background)
        dispatch(fetchSimilarityThresholdsInfo(datasetId!) as any),
        refetchUserConfig(),
      ]);
      setCommonDataFetched(true);
    } catch (error) {}
  };

  const fetchForDataPage = (datasetID: string) => {
    dispatch(setCardslistLoading(true));
    dispatch(setSimilarityData(null));
    dispatch(fetchDataset(datasetId) as any);
    dispatch(fetchDataPagePagination(datasetID, queryParamsFilters) as any);
    dispatch(fetchSimilarityData(datasetId, queryParamsFilters) as any);
  };
  const fetchForClusterPage = (datasetID: string, clusterID: string) => {
    dispatch(fetchDataset(datasetId, clusterId) as any);
    dispatch(
      fetchClusterPagePagination(
        datasetID,
        clusterID,
        queryParamsFilters,
      ) as any,
    );
    dispatch(
      fetchNavigationCluster(datasetID, clusterID, queryParamsFilters) as any,
    );
  };
  const fetchForImagePage = (imageID: string) => {
    dispatch(fetchDataset(datasetId) as any);
    dispatch(fetchImageInfo(datasetId!, imageID, selectedLabelID) as any);
    dispatch(
      fetchImageUniquenessData(datasetId!, imageID, isUniquenessEnabled) as any,
    );
  };

  useEffect(() => {
    fetchCommonData();
    //eslint-disable-next-line
  }, []);

  // URL initialization (runs once to set page=1 if missing)
  useEffect(() => {
    if (!searchParams.has(PAGE_PARAMETER) && !isImagePage) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(PAGE_PARAMETER, "1");

      let path = `${location.pathname}?${newSearchParams.toString()}`;
      navigate(path, { replace: true });
    }
    //eslint-disable-next-line
  }, [location.pathname, isImagePage, navigate]);

  const searchParamsForLoadingData = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete(SEED_PARAMETER);
    params.delete(FLOW_PARAMETER);
    return params.toString();
  }, [searchParams]);

  useEffect(() => {
    const run = async () => {
      if (
        commonDataFetched &&
        (searchParams.has(PAGE_PARAMETER) || isImagePage)
      ) {
        const params = createParams(searchParams);

        if (isImagePage) {
          fetchForImagePage(imageId);
          dispatch(setNavigationContext(IMAGE_DETAILS_PAGE) as any);
          dispatch(fetchImageCustomMetadata(datasetId!, imageId!) as any);
          if (clusterId) {
            dispatch(
              fetchNavigationCluster(
                datasetId!,
                clusterId,
                queryParamsFilters,
              ) as any,
            );
          }
        } else if (params[PAGE_PARAMETER] || params[VERTEX_ID]) {
          if (isClusterPage) {
            fetchForClusterPage(datasetId, clusterId);
            dispatch(setNavigationContext(CLUSTER_DETAILS_PAGE) as any);
          } else if (isDataPage) {
            dispatch(setNavigationCluster(null));
            fetchForDataPage(datasetId);
            dispatch(setNavigationContext(DATA_PAGE) as any);
          }
        }
      }
    };

    run();
    // eslint-disable-next-line
  }, [
    commonDataFetched,
    datasetId,
    clusterId,
    imageId,
    searchParamsForLoadingData,
  ]);

  useEffect(() => {
    const handlePopState = () => {
      //applies scroll on back navigation from browser
      dispatch(setScrollApplied(false));
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      dispatch(resetStore());
    };
  }, [dispatch]);

  const currentVertexCluster = useSelector(getSimilarityVertexCluster);
  useEffect(() => {
    const hasVertexParams =
      queryParamsFilters[VERTEX_ID] || queryParamsFilters[VERTEX_TYPE];

    if (!hasVertexParams && currentVertexCluster) {
      // Clear vertex state when vertex parameters are not present in URL but state still exists
      // This handles browser navigation where URL changes but Redux state doesn't
      dispatch(setSimilarityVertex(null));
    }
  }, [queryParamsFilters, currentVertexCluster, dispatch]);

  return (
    <DataPageRightPanelProvider>
      <VLChatWrapper>
        <Helmet>
          <title>Dataset Exploration</title>
        </Helmet>
        <div className={styles.headerContainer}>
          <ContainerHeader
            dynamicPage={dynamicPage}
            isCardListLoading={isCardListLoading}
            navigationClusterLoader={navigationClusterLoader}
          />
        </div>

        {showRequestDemoModal.flag && (
          <RequestDemoModal calledFrom={showRequestDemoModal.calledFrom} />
        )}
        {showLearnMoreModal && <LearnMoreModal />}
        {dynamicPage === IMAGE_DETAILS_PAGE ? (
          <ImageView labels={labels} imageData={imageData} />
        ) : (
          <>
            <CardsView
              clusters={clusters}
              dynamicPage={dynamicPage}
              navigationCluster={navigationCluster}
              isCardListLoading={isCardListLoading}
              navigationClusterLoader={navigationClusterLoader}
              metadataSummaryEnabled
              isImageUploading={isImageUploading}
              setReviewCounters={setReviewCounters}
              reviewCounters={reviewCounters}
            />
          </>
        )}
        <SignupTeaserModal />
        <AddMediaCompletedModal />
      </VLChatWrapper>
    </DataPageRightPanelProvider>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    isCardListLoading: getIsCardslistLoading(state),
    clusters: getFilteredSimilarityClusters(state),
    navigationCluster: getFilteredNavigationCluster(state),
    navigationClusterLoader:
      getNavigationClusterLoader(state) || !getTotalPagesForCluster(state),
    imageData: getImageData(state),
    labels: getImageLabels(state),
    isImageUploading: getIsImageUploading(state),
    showRequestDemoModal: getShowRequestDemoModal(state),
    showLearnMoreModal: getShowLearnMoreModal(state),
  };
};

export default connect(mapStatesToProps)(DataPage);

interface DataPageProps {
  labels: any;
  clusters: any;
  imageData: any;
  navigationCluster: any;
  isImageUploading: boolean;
  isCardListLoading: boolean;
  navigationClusterLoader: boolean;
  showRequestDemoModal: { flag: boolean; calledFrom: ContactUsComponentType };
  showLearnMoreModal: boolean;
}
